// A new assignment (TZ 8, 15.3; STAGE_5 5.3): the whole screen, the address and the
// reason, a sound and a buzz every two seconds until the crew taps "Accepted" - which
// the operator sees on the desk (accepted_at on the crew's call card).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Flex, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';

import { beep, unlockAudio } from '@/features/dispatch/alarmAudio';
import { DeskStyles } from '@/features/dispatch/DeskStyles';
import { ru } from '@/i18n/ru';
import { ALERT_COLOR } from '@/lib/statusColors';

import { CALLS_KEY, type CrewCall } from './crewApi';
import { crewActions } from './crewActions';
import { patientText } from './crewCallFormat';
import { MainButton } from './CrewScreen';
import { useCrewFail } from './useCrewFail';

const k = ru.crew.calls;
/** How often the alert repeats while nobody has tapped "Accepted". */
export const ALERT_EVERY_MS = 2000;
const BUZZ = [300, 150, 300];

function alert(): void {
  beep();
  try {
    navigator.vibrate(BUZZ);
  } catch {
    // No vibration motor, or the browser keeps it to itself.
  }
}

export function NewCallScreen({ call }: { call: CrewCall }) {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const fail = useCrewFail();
  const queryClient = useQueryClient();
  const [now] = useState(() => Date.now());

  // Sound and buzz at once, then every two seconds; the tap that accepts also opens
  // audio for the next time, where the browser still waits for one.
  useEffect(() => {
    alert();
    const timer = window.setInterval(alert, ALERT_EVERY_MS);
    const open = () => void unlockAudio();
    document.addEventListener('pointerdown', open);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('pointerdown', open);
      try {
        navigator.vibrate(0);
      } catch {
        // As above.
      }
    };
  }, [call.id]);

  const accept = useMutation({
    mutationFn: () => crewActions.accept(call.id, call.kv_seq),
    onSuccess: (sent) => {
      void message.success(sent.queued ? ru.crew.queued : k.accepted);
      void queryClient.invalidateQueries({ queryKey: CALLS_KEY });
    },
    onError: fail,
  });

  return (
    <div
      role="alertdialog"
      aria-modal
      aria-label={k.newCall}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: token.colorBgLayout,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: 24,
      }}
    >
      <Flex vertical align="center" justify="center" gap={8} style={{ flex: 1, textAlign: 'center' }}>
        <DeskStyles />
        <div
          className="sos-alarm-pulse"
          aria-hidden
          style={{ width: 96, height: 96, borderRadius: '50%', background: ALERT_COLOR, marginBottom: 16 }}
        />
        <Typography.Text type="secondary" style={{ fontSize: 16 }}>
          {k.urgency(ru.dict.urgency[call.urgency])}
        </Typography.Text>
        <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2 }}>{call.address}</div>
        <div style={{ fontSize: 18 }}>
          {ru.dispatch.join(call.reason_extra ? `${call.reason}, ${call.reason_extra}` : call.reason, patientText(call, now))}
        </div>
      </Flex>
      <MainButton
        loading={accept.isPending}
        onClick={() => {
          accept.mutate();
        }}
      >
        {k.accept}
      </MainButton>
    </div>
  );
}
